import React, { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import { api, humanBytes, formatApiError } from "../lib/api";
import { Link } from "react-router-dom";
import { Plus, Users, FolderKanban, FileText, Trash2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogTrigger,
} from "../components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function CustomersPage() {
    const { user } = useAuth();
    const [customers, setCustomers] = useState(null);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        name: "",
        company: "",
        contact_email: "",
        storage_quota_gb: 5,
        notes: "",
    });
    const [submitting, setSubmitting] = useState(false);

    const load = () => {
        api.get("/customers")
            .then((r) => setCustomers(r.data))
            .catch(() => setCustomers([]));
    };
    useEffect(load, []);

    const create = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const payload = {
                name: form.name,
                company: form.company || null,
                contact_email: form.contact_email || null,
                storage_quota_gb: Number(form.storage_quota_gb) || null,
                notes: form.notes || null,
            };
            await api.post("/customers", payload);
            toast.success("Klant aangemaakt");
            setOpen(false);
            setForm({ name: "", company: "", contact_email: "", storage_quota_gb: 5, notes: "" });
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSubmitting(false);
        }
    };

    const remove = async (id) => {
        try {
            await api.delete(`/customers/${id}`);
            toast.success("Klant verwijderd");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const isAdmin = user?.role === "admin";

    return (
        <AppShell>
            <div className="px-4 py-6 md:px-10 md:py-10 max-w-[1500px]" data-testid="customers-page">
                <header className="flex items-end justify-between gap-4 flex-wrap mb-8 md:mb-10">
                    <div>
                        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">
                            02 — Klantenportaal
                        </div>
                        <h1 className="font-display text-4xl md:text-5xl tracking-tighter font-black leading-none">
                            Klanten
                        </h1>
                        <p className="text-zinc-500 mt-2 text-sm">
                            {customers?.length || 0} klanten — beheer projecten en bestanden per klant.
                        </p>
                    </div>
                    {isAdmin && (
                        <Dialog open={open} onOpenChange={setOpen}>
                            <DialogTrigger asChild>
                                <Button
                                    className="bg-[#0044FF] hover:bg-[#0033CC] rounded-md"
                                    data-testid="customers-new-button"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Nieuwe klant
                                </Button>
                            </DialogTrigger>
                            <DialogContent>
                                <DialogHeader>
                                    <DialogTitle className="font-display tracking-tight text-2xl">
                                        Nieuwe klant
                                    </DialogTitle>
                                </DialogHeader>
                                <form onSubmit={create} className="space-y-4" data-testid="new-customer-form">
                                    <Field label="Naam *">
                                        <Input
                                            required
                                            value={form.name}
                                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                                            data-testid="new-customer-name"
                                        />
                                    </Field>
                                    <Field label="Bedrijf">
                                        <Input
                                            value={form.company}
                                            onChange={(e) => setForm({ ...form, company: e.target.value })}
                                            data-testid="new-customer-company"
                                        />
                                    </Field>
                                    <Field label="Contact e-mail">
                                        <Input
                                            type="email"
                                            value={form.contact_email}
                                            onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                                            data-testid="new-customer-email"
                                        />
                                    </Field>
                                    <Field label="Opslagquotum (GB)">
                                        <Input
                                            type="number"
                                            min="1"
                                            value={form.storage_quota_gb}
                                            onChange={(e) => setForm({ ...form, storage_quota_gb: e.target.value })}
                                            data-testid="new-customer-quota"
                                        />
                                    </Field>
                                    <Field label="Notities">
                                        <Textarea
                                            rows={3}
                                            value={form.notes}
                                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                            data-testid="new-customer-notes"
                                        />
                                    </Field>
                                    <DialogFooter>
                                        <Button
                                            type="submit"
                                            disabled={submitting}
                                            className="bg-[#0044FF] hover:bg-[#0033CC] rounded-md"
                                            data-testid="new-customer-submit"
                                        >
                                            {submitting ? "Bezig…" : "Klant aanmaken"}
                                        </Button>
                                    </DialogFooter>
                                </form>
                            </DialogContent>
                        </Dialog>
                    )}
                </header>

                {!customers ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="h-44 bg-white border border-zinc-200 rounded-md animate-pulse" />
                        ))}
                    </div>
                ) : customers.length === 0 ? (
                    <div className="border border-dashed border-zinc-300 rounded-md py-20 text-center bg-white">
                        <Users className="w-10 h-10 text-zinc-300 mx-auto mb-4" />
                        <div className="font-display text-2xl tracking-tight mb-2">Nog geen klanten</div>
                        <p className="text-sm text-zinc-500 mb-6">
                            Maak je eerste klant aan om projecten en bestanden te organiseren.
                        </p>
                        {isAdmin && (
                            <Button
                                onClick={() => setOpen(true)}
                                className="bg-[#0044FF] hover:bg-[#0033CC] rounded-md"
                                data-testid="customers-empty-cta"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Nieuwe klant
                            </Button>
                        )}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {customers.map((c) => {
                            const pct = c.storage_quota_bytes
                                ? Math.min(100, (c.storage_used_bytes / c.storage_quota_bytes) * 100)
                                : 0;
                            return (
                                <div
                                    key={c.id}
                                    className="bg-white border border-zinc-200 rounded-md p-6 hover:border-[#0044FF] hover:shadow-md transition-all flex flex-col gap-4"
                                    data-testid={`customer-card-${c.id}`}
                                >
                                    <Link to={`/customers/${c.id}`} className="block flex-1">
                                <div className="flex items-start justify-between mb-3">
                                    {c.logo_url ? (
                                        <img
                                            src={c.logo_url}
                                            alt={c.name}
                                            className="w-10 h-10 rounded-sm object-cover border border-zinc-200 bg-white"
                                        />
                                    ) : (
                                        <div className="w-10 h-10 bg-[#09090b] text-white rounded-sm flex items-center justify-center font-display font-black text-base">
                                            {(c.name || "?").slice(0, 1).toUpperCase()}
                                        </div>
                                    )}
                                    <span className="text-[10px] font-mono uppercase text-zinc-500">
                                        {new Date(c.created_at).toLocaleDateString("nl-BE")}
                                    </span>
                                </div>
                                        <div className="font-display text-xl font-bold tracking-tight">{c.name}</div>
                                        {c.company && <div className="text-xs text-zinc-500 mt-0.5">{c.company}</div>}
                                        {c.contact_email && (
                                            <div className="text-xs font-mono text-zinc-500 mt-1 truncate">
                                                {c.contact_email}
                                            </div>
                                        )}
                                    <div className="flex items-center gap-4 mt-4 text-xs text-zinc-600">
                                            <span className="inline-flex items-center gap-1">
                                                <FolderKanban className="w-3 h-3" /> {c.project_count} projecten
                                            </span>
                                            <span className="inline-flex items-center gap-1">
                                                <FileText className="w-3 h-3" /> {c.file_count} bestanden
                                            </span>
                                            {c.ai_call_count > 0 && (
                                                <span className="inline-flex items-center gap-1 text-[#0044FF] font-mono">
                                                    AI: €{((c.ai_cost_usd || 0) * 0.92).toFixed(3)}
                                                </span>
                                            )}
                                        </div>
                                    </Link>
                                    <div>
                                        <div className="flex items-center justify-between text-xs font-mono text-zinc-500 mb-1.5">
                                            <span>{humanBytes(c.storage_used_bytes)}</span>
                                            <span>{humanBytes(c.storage_quota_bytes)}</span>
                                        </div>
                                        <div className="h-1 bg-zinc-200 rounded-full overflow-hidden">
                                            <div
                                                className={`h-full ${pct > 90 ? "bg-red-500" : pct > 75 ? "bg-amber-500" : "bg-[#0044FF]"}`}
                                                style={{ width: `${pct}%` }}
                                            />
                                        </div>
                                    </div>
                                    {isAdmin && (
                                        <div className="flex justify-end pt-2 border-t border-zinc-100">
                                            <AlertDialog>
                                                <AlertDialogTrigger asChild>
                                                    <button
                                                        className="text-zinc-400 hover:text-red-600 p-1.5 rounded hover:bg-zinc-100"
                                                        data-testid={`customer-delete-${c.id}`}
                                                        aria-label="Verwijderen"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </AlertDialogTrigger>
                                                <AlertDialogContent>
                                                    <AlertDialogHeader>
                                                        <AlertDialogTitle>Klant verwijderen?</AlertDialogTitle>
                                                        <AlertDialogDescription>
                                                            Dit verwijdert "{c.name}" en al hun projecten en bestanden permanent.
                                                            Deze actie kan niet ongedaan gemaakt worden.
                                                        </AlertDialogDescription>
                                                    </AlertDialogHeader>
                                                    <AlertDialogFooter>
                                                        <AlertDialogCancel>Annuleer</AlertDialogCancel>
                                                        <AlertDialogAction
                                                            onClick={() => remove(c.id)}
                                                            className="bg-red-600 hover:bg-red-700"
                                                        >
                                                            Verwijderen
                                                        </AlertDialogAction>
                                                    </AlertDialogFooter>
                                                </AlertDialogContent>
                                            </AlertDialog>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </AppShell>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-[0.18em] text-zinc-500 mb-1.5 block">
                {label}
            </label>
            {children}
        </div>
    );
}
